import { useQuery } from '@tanstack/react-query';

import { contentTypesDelete, contentTypesUsage } from '@/shared/api/generated/content/content';
import type { TypeBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { pluralize } from '@/shared/lib/plural';
import { ConfirmDialog } from '@/shared/ui/Dialog';
import { toast } from '@/shared/ui/Toast';

import { RECORDS, RECORDS_IN } from './kinds';
import type { Usage } from './usage';

/**
 * Удаление типа с последствиями: сколько записей уйдёт в корзину, а у
 * блока — на скольких страницах он стоит.
 */
export function RemoveTypeDialog({
  type,
  onClose,
  onRemoved,
}: {
  type: TypeBody | undefined;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const usage = useQuery({
    queryKey: ['content-type', type?.id, 'usage'],
    enabled: type !== undefined,
    queryFn: async () => {
      const response = await contentTypesUsage(type?.id ?? '');

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос использования');
      }

      return response.data;
    },
  });

  return (
    <ConfirmDialog
      open={type !== undefined}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      title={`Убрать «${type?.name ?? ''}» в корзину?`}
      description={
        type === undefined || usage.data === undefined
          ? 'Считаем, что заденет удаление…'
          : removalText(type, usage.data)
      }
      confirmLabel="В корзину"
      tone="danger"
      onConfirm={async () => {
        if (type === undefined) {
          return;
        }

        try {
          await contentTypesDelete(type.id);
          toast.success(type.kind === 'block' ? 'Компонент в корзине' : 'Модель в корзине');
          onRemoved();
        } catch (cause) {
          toast.error('Не удалось удалить', {
            description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
          });

          throw cause;
        }
      }}
    />
  );
}

function removalText(type: TypeBody, usage: Usage): string {
  if (type.kind === 'block') {
    const records = (usage.used_in ?? []).reduce((sum, place) => sum + place.records, 0);

    return records === 0
      ? 'Блок нигде не стоит. Он уедет в корзину, вернуть можно.'
      : `Блок стоит в ${pluralize(records, RECORDS_IN)}. Там он останется и будет сохраняться, но вставить его заново будет нельзя, пока не вернёте из корзины.`;
  }

  if (usage.records === 0) {
    return 'Записей у типа нет. Он уедет в корзину, вернуть можно.';
  }

  const published =
    usage.published > 0
      ? `, из них опубликованных — ${String(usage.published)}: с сайта они пропадут`
      : '';

  return `Вместе с типом в корзину уйдут ${pluralize(usage.records, RECORDS)}${published}. Вернуть можно из корзины типов.`;
}
